/**
 * ProductionTab Component
 * Year-specific production workflow data entry form
 */
import React, { memo } from 'react';
import { Button } from '../ui/button';
import { Save, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SalesField, DateField } from './SalesTab';
import { SelectField } from './FieldOptions';

export const ProductionTab = memo(({ 
  salesYear,
  salesData, 
  salesLoading, 
  salesDirty, 
  savingSales, 
  saveSalesData, 
  updateSalesFormRef 
}) => {
  if (salesLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" key={`prod-${salesData.org_id || 'unknown'}-${salesYear}-${salesData.sales_id || 'new'}`}>
      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          size="sm" 
          onClick={saveSalesData}
          disabled={!salesDirty || savingSales}
          className={cn(salesDirty && "animate-pulse")}
        >
          {savingSales ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          {salesDirty ? 'Save Changes' : 'Saved'}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-1">
          <SalesField label="Front Cover" field="front_cover" defaultValue={salesData.front_cover} onFieldChange={updateSalesFormRef} />
          <SalesField label="Template" field="template" defaultValue={salesData.template} onFieldChange={updateSalesFormRef} />
          <SelectField label="Logo on File" field="logo_on_file" defaultValue={salesData.logo_on_file} onFieldChange={updateSalesFormRef} />
          <SalesField label="# of Logos Required" field="numbers_of_logos_required" defaultValue={salesData.numbers_of_logos_required} onFieldChange={updateSalesFormRef} />
          <SelectField label="Inserts" field="inserts" defaultValue={salesData.inserts} onFieldChange={updateSalesFormRef} />
          <SelectField label="Sponsors" field="sponsors" defaultValue={salesData.sponsors} onFieldChange={updateSalesFormRef} />
          <DateField label="Date Envelopes Sent" field="date_envelopes_sent" defaultValue={salesData.date_envelopes_sent} onFieldChange={updateSalesFormRef} />
          <SalesField label="Add Info from Shoot" field="additional_info_from_shoot" defaultValue={salesData.additional_info_from_shoot} onFieldChange={updateSalesFormRef} />
          <SalesField label="Production Special Inst" field="production_special_instructions" defaultValue={salesData.production_special_instructions} onFieldChange={updateSalesFormRef} />
          <DateField label="Presentation Day" field="presentation_day" defaultValue={salesData.presentation_day} onFieldChange={updateSalesFormRef} />
          <DateField label="Job Sheet Printed" field="job_sheet_printed" defaultValue={salesData.job_sheet_printed} onFieldChange={updateSalesFormRef} />
          <DateField label="Date Due Out" field="date_due_out" defaultValue={salesData.date_due_out} onFieldChange={updateSalesFormRef} />
          <DateField label="Date Downloaded" field="date_downloaded" defaultValue={salesData.date_downloaded} onFieldChange={updateSalesFormRef} />
          <SalesField label="Downloaded By" field="downloaded_by" defaultValue={salesData.downloaded_by} onFieldChange={updateSalesFormRef} />
          <SalesField label="# of Group Files" field="num_of_group_files" defaultValue={salesData.num_of_group_files} onFieldChange={updateSalesFormRef} />
          <SalesField label="# of Portrait Files" field="num_of_portrait_files" defaultValue={salesData.num_of_portrait_files} onFieldChange={updateSalesFormRef} />
        </div>
        <div className="space-y-1">
          <DateField label="Date Ripped" field="date_ripped" defaultValue={salesData.date_ripped} onFieldChange={updateSalesFormRef} />
          <SalesField label="Ripped By" field="ripped_by" defaultValue={salesData.ripped_by} onFieldChange={updateSalesFormRef} />
          <SalesField label="# Teams/Classes Ripped" field="num_of_teams_classes_ripped" defaultValue={salesData.num_of_teams_classes_ripped} onFieldChange={updateSalesFormRef} />
          <SelectField label="Names Typed" field="names_typed" defaultValue={salesData.names_typed} onFieldChange={updateSalesFormRef} />
          <SalesField label="Typed By" field="typed_by" defaultValue={salesData.typed_by} onFieldChange={updateSalesFormRef} />
          <DateField label="Date Names Typed" field="date_names_typed" defaultValue={salesData.date_names_typed} onFieldChange={updateSalesFormRef} />
          <SelectField label="Job Assigned To" field="job_assigned_to" defaultValue={salesData.job_assigned_to} onFieldChange={updateSalesFormRef} />
          <DateField label="Date Job Assigned" field="date_job_assigned" defaultValue={salesData.date_job_assigned} onFieldChange={updateSalesFormRef} />
          {/* Need By — hard deadline the client needs the job by, with a
              short note so Production can see context at a glance. Stacked
              under Date Job Assigned per user request (Feb 2026). */}
          <DateField label="Need By Date" field="need_by_date" defaultValue={salesData.need_by_date} onFieldChange={updateSalesFormRef} />
          <SalesField label="Need By Note" field="need_by_note" defaultValue={salesData.need_by_note} onFieldChange={updateSalesFormRef} />
          <DateField label="Date Job Printed" field="date_job_printed" defaultValue={salesData.date_job_printed} onFieldChange={updateSalesFormRef} />
          <SalesField label="Date ID Card Printed" field="date_id_card_printed" defaultValue={salesData.date_id_card_printed} onFieldChange={updateSalesFormRef} />
          <SalesField label="# of ID Cards Printed" field="num_of_id_cards_printed" defaultValue={salesData.num_of_id_cards_printed} onFieldChange={updateSalesFormRef} />
          <SalesField label="Late ID Cards" field="late_id_cards" defaultValue={salesData.late_id_cards} onFieldChange={updateSalesFormRef} />
          <SalesField label="Assigned Packer" field="assigned_packer" defaultValue={salesData.assigned_packer} onFieldChange={updateSalesFormRef} />
          <DateField label="Date Job Packed" field="date_job_packed" defaultValue={salesData.date_job_packed} onFieldChange={updateSalesFormRef} />
          <DateField label="Dispatch Date" field="dispatch_date" defaultValue={salesData.dispatch_date} onFieldChange={updateSalesFormRef} />
          <SelectField label="Dispatch Type" field="dispatch_type" defaultValue={salesData.dispatch_type} onFieldChange={updateSalesFormRef} />
          <SalesField label="Courier Coupon #" field="courier_coupon_num" defaultValue={salesData.courier_coupon_num} onFieldChange={updateSalesFormRef} />
        </div>
      </div>
    </div>
  );
});

ProductionTab.displayName = 'ProductionTab';

export default ProductionTab;
